import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import * as Yup from 'yup'
import {useFormik} from 'formik'
import clsx from 'clsx';
import { useUserLoader } from '../../../../core/users';
import { useNavigate } from "react-router-dom";
import {
  DateSelector,
  FileUploader,
  FileUploaderResponse,
  FormikNotesComponent,
  StatusErrorMessage
} from '../../../../../components/widgets';

import { format } from 'date-fns';

import { doFormSubmit, isPopulatedArray, parseTimestamp, processNoteValues, reloadDraftVars } from '../../../../../components/helpers';
import { PhaseFormObserver } from '../../components/PhaseFormObserver';
import { Application } from '../../../../core/applications';
import { SubmitButtonComponent } from '../../../../../components/layout';
import { pickBy } from 'lodash';

const adminSetupSchema: any = Yup.object({    
  inspectorForCocId: Yup.number().integer().label('CoC Inspector'),
  inspectorForSamplingId: Yup.number().integer().label('Sampling Inspector'),
  inspectorForInspectionId: Yup.number().integer().label('Inspection Inspector'),
  attachmentId: Yup.number().integer().label('Audit Kit Attachment'),
  attachmentName: Yup.string().trim().label('Attachment Name'),
  samplingAttachmentId: Yup.number().integer().label('Sampling Atachment'),
  samplingAttachmentName: Yup.string().trim().label('Sampling Attachment Name'),
  cocAttachmentId: Yup.number().integer().label('Draft COC Template'),
  cocAttachmentName: Yup.string().trim().label('Draft COC Template Name'),
  dateOfSampling: Yup.string().trim().label('Sampling Date'),
  inspectionLocation: Yup.string().trim().label('Location'),
  inspectionDate: Yup.string().trim().label('Inspection Date'),
  note: Yup.string().trim().label('Note'),
  noteAttachments: Yup.array(),
});



type Props = {
  appData: Application
  draftVars: Record<string, any>
  doClose: Function
}

const InspectionAdminSetupForm : FC<Props> = ({ appData, draftVars, doClose }) => {
  console.log(draftVars);

  const navigate = useNavigate();

  const [ inspect_dt , setInspectDate ] = useState<Date | null>(null);
  const [ sampling_dt , setSamplingDate ] = useState<Date | null>(null);    

  console.log(inspect_dt);
      
  const {appID, productTypeId, manufacturerAddress} = useMemo(() => {
    if (! appData) return {
      appID : 0, 
      productTypeId: 0, 
      manufacturerAddress: '',     
    };

    let manufacturerAddress = '';
    if (appData?.manufacturers && isPopulatedArray(appData.manufacturers)) {
      manufacturerAddress = appData.manufacturers[0].manufacturerAddress;
    } else if (Object.hasOwn(appData,'manufacturerAddress')) {
      // @ts-ignore
      manufacturerAddress = appData.manufacturerAddress;
    }

    return {
      appID: appData?.id ?? 0,
      productTypeId: appData?.productType?.id ?? 0,
      manufacturerAddress,            
    }
  }, [appData]);
  
  const { data: assignees } = useUserLoader({userType: 'CAMS Inspector', productTypeId });

  const initValues = useMemo(() => {

    let initValues = {
      inspectorForCocId: 0,
      inspectorForSamplingId: 0,
      inspectorForInspectionId: 0,
      attachmentId: 0,
      attachmentName: '',
      samplingAttachmentId: 0,
      samplingAttachmentName: '',
      cocAttachmentId: 0,
      cocAttachmentName: '',
      dateOfSampling: '',
      inspectionLocation: '',
      inspectionDate: '',
      note: '',
      noteAttachments: '',
    };
        
    reloadDraftVars(initValues, draftVars, ['applicationid']);

    return initValues;

  }, [draftVars])
  
  const formik = useFormik({
    initialValues: initValues,
    validationSchema: adminSetupSchema,
    enableReinitialize: true, 
    validateOnMount: true,
    onSubmit: async(values, {setStatus, setSubmitting}) => {

      let payload = {
        ...pickBy(values,(value,key) => (value && (! ['note','noteAttachments','attachmentName','samplingAttachmentName','cocAttachmentName'].includes(key)))),
        applicationid : appID,
      };

      processNoteValues(values, payload);
      
      await doFormSubmit('Phase_5_1Servlet', payload, setStatus, setSubmitting, () => {
        navigate('/setsco/application-management/applications');
      });
    }
  })

  const {
    attachmentName,
    samplingAttachmentName,
    cocAttachmentName
  } = formik.values;

  const { setFieldValue } = formik;
  
  const setFormikDate = useCallback( (fieldName: string, dt: Date | null) => {
    const formattedDate = dt ? format(dt, "yyyy-MM-dd'T'HH:mm:ssxx") : '';
    // Passing `true` as the 3rd argument explicitly triggers field validation
    setFieldValue(fieldName, formattedDate, true);
  }, [setFieldValue]);

  // 2. Clean callback handlers without `any` or disabled linter rules
  const onSetSamplingDate = useCallback( (dt: Date | null) => {
    setSamplingDate(dt);
    setFormikDate('dateOfSampling', dt);
  }, [setSamplingDate, setFormikDate] );
  
  const onSetInspectionDate = useCallback( (dt: Date | null) => {
    console.log(dt);
    setInspectDate(dt);
    setFormikDate('inspectionDate', dt);
  }, [setInspectDate, setFormikDate] );

  useEffect(() => {
    setFieldValue('inspectionLocation', manufacturerAddress);    
  }, [manufacturerAddress, setFieldValue]);

  // reload draft values
  useEffect(() => {
    if (! (draftVars && typeof draftVars === 'object')) return;
    if (draftVars?.dateOfSampling) {
      setSamplingDate(parseTimestamp(draftVars?.dateOfSampling));
    }
    if (draftVars?.inspectionDate) {
      setInspectDate(parseTimestamp(draftVars?.inspectionDate));
    }    
  }, [draftVars])

  return (
  <>
  <PhaseFormObserver appID={appID} values={formik.values} isSubmitting={formik.isSubmitting} />
  <form id='kt_inspection_admin_setup_form' className='form' onSubmit={formik.handleSubmit} noValidate>    
    <div
      className='d-flex flex-column gap-2 mb-6 px-2'
      id='kt_inspection_admin_setup_scroll'
      data-kt-scroll='true'
      data-kt-scroll-activate='{default: false, lg: true}'
      data-kt-scroll-max-height='auto'
      data-kt-scroll-dependencies='#kt_inspection_admin_setup_header'
      data-kt-scroll-wrappers='#kt_inspection_admin_setup_scroll'
      data-kt-scroll-offset='300px'
    >

      <div className='fv-row'>
        <label className='col-form-label fw-bold fs-6'>
          <span>Factory Audit Location</span>
        </label>
        <textarea
          placeholder='Enter location'
          {...formik.getFieldProps('inspectionLocation')}
          name='inspectionLocation'
          className={clsx(
              'form-control form-control-solid mx-1',
              {'is-invalid': formik.touched.inspectionLocation && formik.errors.inspectionLocation},
              {
                'is-valid': formik.touched.inspectionLocation && !formik.errors.inspectionLocation,
              }
          )}
          rows={1}
        ></textarea>
        {(formik.errors.inspectionLocation && formik.errors.inspectionLocation.length > 0) && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>{formik.errors.inspectionLocation}</div>
        </div>
        )}
      </div>
      <div className='separator border-gray-200 my-2'></div>
      <div className="row">
        <div className="col-6">
          <label className='fw-bold fs-6 mb-2'>Assign Inspection Inspector : </label>
          <div className='d-flex fv-row'>
            <select
              className='form-select form-select-solid fw-bold mb-2 fs-7 py-2'
              {...formik.getFieldProps('inspectorForInspectionId')}
              name='inspectorForInspectionId'
              disabled={formik.isSubmitting}
            >
              <option value=''>Select Inspection Inspector ...</option>
              {assignees && assignees.map( dat => (
                <option key={`inspection${dat.value}`} value={dat.value}>{dat.text}</option>
              ))}
            </select>
          </div>
          {formik.touched.inspectorForInspectionId && formik.errors.inspectorForInspectionId && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              {formik.errors.inspectorForInspectionId}
            </div>
          </div>
          )}
        </div>
        <div className="col-6">
          <label className='fw-bold fs-6 mb-2'>Date of Audit</label>
          <DateSelector
            className='form-control form-control-sm form-control-solid'
            selected={inspect_dt}
            onChange={onSetInspectionDate}
            showShiftSelector={true}  
            dateFormat = 'LL'
          ></DateSelector>
          {formik.touched.inspectionDate && formik.errors.inspectionDate && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.inspectionDate}</span>
            </div>
          </div>
          )}
        </div>
      </div>            
      <div className="fv-row">
        <FileUploader
          required={false}
          mainTitle="Audit Kit"
          subTitle={ attachmentName ? attachmentName : "Click or drop here to upload."}
          fileName={ attachmentName }
          allowMultiple={false}
          acceptFiles="UserFileFormats"
          onFileUploaded={ (dat: FileUploaderResponse) => {
            formik.setFieldValue("attachmentId", dat.id, false);
            formik.setFieldValue("attachmentName", dat.name, false);
            setTimeout(() => formik.validateField("attachmentId"), 100)
          }}
          onClearFile={ () => {
            formik.setFieldValue("attachmentId", 0, false);
            formik.setFieldValue("attachmentName", '', false);
            setTimeout(() => formik.validateField("attachmentId"), 100)
          }}
        />
        {formik.touched.attachmentId && formik.errors.attachmentId && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>
              <span role='alert'>{formik.errors.attachmentId}</span>
          </div>
        </div>
        )}
      </div>

      <div className='separator border-gray-200 my-2'></div>

      <div className="row">
        <div className="col-6">
          <label className='fw-bold fs-6 mb-2'>Assign CoC Inspector : </label>
          <div className='d-flex fv-row'>
            <select
              className='form-select form-select-solid fw-bold mb-2 fs-7 py-2'
              {...formik.getFieldProps('inspectorForCocId')}
              name='inspectorForCocId'
              disabled={formik.isSubmitting}
            >
              <option value=''>Select CoC Inspector ...</option>
              {assignees && assignees.map( dat => (
                <option key={`cocinspector${dat.value}`} value={dat.value}>{dat.text}</option>
              ))}
            </select>
          </div>
          {formik.touched.inspectorForCocId && formik.errors.inspectorForCocId && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              {formik.errors.inspectorForCocId}
            </div>
          </div>
          )}
        </div>                
        <div className="col-6"></div>                
      </div>
      <div className="fv-row">
        <FileUploader
          required={false}
          mainTitle="Draft COC Template"
          subTitle={ cocAttachmentName ? cocAttachmentName : "Click or drop here to upload."}
          fileName={ cocAttachmentName }
          allowMultiple={false}
          acceptFiles="UserFileFormats"
          onFileUploaded={ (dat: FileUploaderResponse) => {
              formik.setFieldValue("cocAttachmentId", dat.id, false);
              formik.setFieldValue("cocAttachmentName", dat.name, false);

              setTimeout(() => formik.validateField("cocAttachmentId"), 100)
          }}
          onClearFile={ () => {
              formik.setFieldValue("cocAttachmentId", 0, false);
              formik.setFieldValue("cocAttachmentName", '', false);
              setTimeout(() => formik.validateField("cocAttachmentId"), 100)
          }}

        />
        {formik.touched.cocAttachmentId && formik.errors.cocAttachmentId && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>
              <span role='alert'>{formik.errors.cocAttachmentId}</span>
          </div>
        </div>
        )}
      </div>

      <div className='separator border-gray-200 my-2'></div>
      
      <div className="row">
        <div className="col-6">
          <label className='fw-bold fs-6 mb-2'>Assign Sampling Inspector : </label>
          <div className='d-flex fv-row'>
            <select
              className='form-select form-select-solid fw-bold mb-2 fs-7 py-2'
              {...formik.getFieldProps('inspectorForSamplingId')}
              name='inspectorForSamplingId'
              disabled={formik.isSubmitting}
            >
              <option value=''>Select Sampling Inspector ...</option>
              {assignees && assignees.map( dat => (
                <option key={`sampleinspector${dat.value}`} value={dat.value}>{dat.text}</option>
              ))}
            </select>
          </div>
          {formik.touched.inspectorForSamplingId && formik.errors.inspectorForSamplingId && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              {formik.errors.inspectorForSamplingId}
            </div>
          </div>
          )}
        </div>
        <div className="col-6">
          <label className='fw-bold fs-6 mb-2'>Date of Sampling</label>
          <DateSelector
            className='form-control form-control-sm form-control-solid'
            selected={sampling_dt}
            onChange={onSetSamplingDate}
            dateFormat="LL"
          ></DateSelector>
          {formik.touched.dateOfSampling && formik.errors.dateOfSampling && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.dateOfSampling}</span>
            </div>
          </div>
          )}
        </div>                
      </div>

      <div className="fv-row">
        <FileUploader
          required={false}
          mainTitle="Sampling Attachment"
          subTitle={ samplingAttachmentName ? samplingAttachmentName : "Click or drop here to upload."}
          fileName={ samplingAttachmentName }
          allowMultiple={false}
          acceptFiles="UserFileFormats"
          onFileUploaded={ (dat: FileUploaderResponse) => {
            formik.setFieldValue("samplingAttachmentId", dat.id, false);
            formik.setFieldValue("samplingAttachmentName", dat.name, false);

            setTimeout(() => formik.validateField("samplingAttachmentId"), 100)
          }}
          onClearFile={ () => {
            formik.setFieldValue("samplingAttachmentId", 0, false);
            formik.setFieldValue("samplingAttachmentName", '', false);
            setTimeout(() => formik.validateField("samplingAttachmentId"), 100)
          }}
        />
        {formik.touched.samplingAttachmentId && formik.errors.samplingAttachmentId && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>
            <span role='alert'>{formik.errors.samplingAttachmentId}</span>
          </div>
        </div>
        )}
      </div>
        
      <FormikNotesComponent formik={formik} />
      
      <div className='separator border-gray-200 mt-2 mb-4'></div>

      <StatusErrorMessage status={formik.status} />
      <SubmitButtonComponent 
        formik={formik}
        onCancel={() => doClose()}        
      />      
    </div>
  </form>
  </>
  )
}


export {InspectionAdminSetupForm}